import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, BookOpen, Palette, FileText, Check, Image as ImageIcon, Mic, Square, Camera, X, Volume2 } from "lucide-react";
import { speechSupport, loadVoices, speak, matchWords, tokenize, createReadingSession, createAudioRecorder } from "./speech";
import { SubjectSelect, GradeSelect } from "./_pickers";

const SUBTABS = [
  { id: "read", label: "Read to Lead", Icon: BookOpen },
  { id: "art", label: "Art Studio", Icon: Palette },
  { id: "notes", label: "Notes & Lessons", Icon: FileText },
];

export default function EducatorPonderrLearn({ profile }) {
  const [sub, setSub] = useState("read");
  const [settings, setSettings] = useState({ enabled_languages: [] });
  const [testTypes, setTestTypes] = useState([]);
  useEffect(() => {
    api.get("/ponderr-learn/settings").then((r) => setSettings(r.data)).catch(() => {});
    api.get("/settings").then((r) => setTestTypes(r.data?.test_types || [])).catch(() => {});
  }, []);
  return (
    <div className="space-y-5">
      <div className="nb-card p-4 bg-[#93C5FD]">
        <div className="nb-label">PonderrLearn · {profile?.class_name || "—"}-{profile?.section || "—"}</div>
        <div className="display text-xl font-bold">Reading, Art & Immersive Notes for your class</div>
      </div>
      <div className="flex gap-2 overflow-x-auto pb-1">
        {SUBTABS.map((t) => (
          <button key={t.id} onClick={() => setSub(t.id)}
            className={`nb-btn text-xs sm:text-sm shrink-0 ${sub === t.id ? "" : "nb-btn-ghost"}`}
            data-testid={`epl-subtab-${t.id}`}><t.Icon size={14} />{t.label}</button>
        ))}
      </div>
      {sub === "read" && <EduRead profile={profile} settings={settings} testTypes={testTypes} />}
      {sub === "art" && <EduArt profile={profile} testTypes={testTypes} />}
      {sub === "notes" && <EduNotes profile={profile} settings={settings} />}
    </div>
  );
}

function EduRead({ profile, settings, testTypes }) {
  const langs = settings.enabled_languages || [];
  const [passages, setPassages] = useState([]);
  const [selPid, setSelPid] = useState("");
  const [roster, setRoster] = useState([]);
  const [subs, setSubs] = useState([]);
  const [scores, setScores] = useState({});
  const [sync, setSync] = useState({});
  const [recording, setRecording] = useState(null); // {student, passage}

  const loadAssignments = () => api.get("/ponderr-learn/educator/assignments").then((r) => {
    setPassages(r.data.passages || []);
    if (!selPid && (r.data.passages || []).length) setSelPid(r.data.passages[0].passage_id);
  }).catch(() => {});
  const loadSubs = () => api.get("/ponderr-learn/educator/reading-submissions").then((r) => setSubs(r.data)).catch(() => {});
  useEffect(() => { loadAssignments(); loadSubs(); }, []);
  useEffect(() => {
    if (profile?.class_name && profile?.section) {
      api.get("/students", { params: { class_name: profile.class_name, section: profile.section } })
        .then((r) => setRoster(r.data)).catch(() => {});
    }
  }, [profile]);

  const selPassage = passages.find((p) => p.passage_id === selPid);
  const subFor = (sid) => subs.filter((s) => s.passage_id === selPid && s.student_id === sid)
    .sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""))[0];

  const approve = async (s) => {
    try {
      await api.post(`/ponderr-learn/educator/reading-submissions/${s.submission_id}/approve`, {
        teacher_score: scores[s.submission_id] != null ? Number(scores[s.submission_id]) : s.ai_suggested_mark,
        test_type: sync[s.submission_id] || null, subject: "Oral Language",
      });
      toast.success("Approved & recorded"); loadSubs();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const [f, setF] = useState({ grade: profile?.class_name || "G01", section: profile?.section || "A", subject_name: profile?.subject || "English", language_code: "en-IN", passage_type: "PASSAGE", title: "", content_text: "", word_items: "", target_wpm: 90, max_marks: 10 });
  useEffect(() => { setF((s) => ({ ...s, grade: profile?.class_name || s.grade, section: profile?.section || s.section, subject_name: profile?.subject || s.subject_name })); }, [profile]);
  const createAssignment = async () => {
    if (!f.title.trim()) return toast.error("Title required");
    try {
      const lang = langs.find((l) => l.code === f.language_code);
      await api.post("/ponderr-learn/passages", { ...f, language_label: lang?.label, word_items: f.passage_type === "WORD_LIST" ? f.word_items.split(/[,\n]/).map((w) => w.trim()).filter(Boolean) : [], content_text: f.passage_type === "PASSAGE" ? f.content_text : "", target_wpm: Number(f.target_wpm), max_marks: Number(f.max_marks) });
      toast.success("Assignment created for your class"); setF({ ...f, title: "", content_text: "", word_items: "" }); loadAssignments();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-5">
      {recording && (
        <ReadingRecordModal student={recording.student} passage={recording.passage} settings={settings}
          onClose={() => setRecording(null)}
          onDone={() => { setRecording(null); loadSubs(); }} />
      )}
      <div className="nb-card p-5 space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="display text-lg font-bold">Reading Assignment Grading Log</div>
          <label className="flex items-center gap-2 text-xs">Assignment:
            <select className="nb-select text-xs" value={selPid} onChange={(e) => setSelPid(e.target.value)} data-testid="epl-read-assignment">
              {passages.map((p) => <option key={p.passage_id} value={p.passage_id}>{p.title} ({p.grade})</option>)}
            </select>
          </label>
        </div>
        {!selPassage ? <div className="text-sm text-slate-500">No reading assignments for your class yet — create one below.</div> : (
          <div className="nb-card p-0 overflow-x-auto">
            <table className="nb-table">
              <thead><tr><th>Student</th><th>Status</th><th>WPM</th><th>Accuracy</th><th>AI Mark</th><th>Your Mark</th><th>Sync Test Type</th><th>Action</th></tr></thead>
              <tbody>
                {roster.map((stu) => {
                  const s = subFor(stu.student_id);
                  const status = s?.status || "NOT_SUBMITTED";
                  return (
                    <tr key={stu.student_id} data-testid={`epl-read-roster-${stu.student_id}`}>
                      <td className="font-bold">{stu.name}</td>
                      <td><span className={`nb-badge ${status === "APPROVED" ? "bg-[#86EFAC]" : status === "PENDING" ? "bg-[#FDE047]" : "bg-slate-200"}`}>{status.replace("_", " ")}</span></td>
                      <td className="mono">{s?.recorded_wpm ?? "—"}</td>
                      <td className="mono">{s ? `${s.accuracy_percent}%` : "—"}</td>
                      <td className="mono font-bold">{s ? `${s.ai_suggested_mark}/${s.max_marks}` : "—"}</td>
                      <td>{s ? <input type="number" className="nb-input w-16 text-sm" defaultValue={s.teacher_score ?? s.ai_suggested_mark} onChange={(e) => setScores({ ...scores, [s.submission_id]: e.target.value })} data-testid={`epl-read-score-${stu.student_id}`} /> : "—"}</td>
                      <td>{s ? (
                        <select className="nb-select text-xs" onChange={(e) => setSync({ ...sync, [s.submission_id]: e.target.value })} data-testid={`epl-read-tt-${stu.student_id}`}>
                          <option value="">— optional —</option>
                          {testTypes.map((t) => <option key={t} value={t}>{t}</option>)}
                        </select>) : "—"}</td>
                      <td>
                        <div className="flex flex-col gap-1 items-start">
                          {s ? <AudioPlayer url={s.audio_url || s.audio_blob_url} testid={`epl-read-audio-${stu.student_id}`} /> : null}
                          {status === "APPROVED" ? <span className="nb-badge bg-[#86EFAC]">✓ Approved</span> : (
                            <div className="flex gap-1 flex-wrap">
                              <button onClick={() => setRecording({ student: stu, passage: selPassage })} className="nb-btn nb-btn-secondary text-xs" data-testid={`epl-read-record-${stu.student_id}`}><Mic size={12} />Record for Student</button>
                              {s && <button onClick={() => approve(s)} className="nb-btn text-xs" data-testid={`epl-read-approve-${stu.student_id}`}><Check size={12} />Approve</button>}
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {roster.length === 0 && <tr><td colSpan={8} className="text-center py-6 text-slate-500">No students in your class roster.</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <div className="nb-card p-5 space-y-3">
        <div className="display text-lg font-bold">➕ Create Class Reading Assignment</div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <label className="block"><span className="nb-label">Grade</span><GradeSelect className="nb-select mt-1" value={f.grade} onChange={(v) => setF({ ...f, grade: v })} data-testid="epl-read-grade" /></label>
          <label className="block"><span className="nb-label">Section</span><input className="nb-input mt-1" value={f.section} onChange={(e) => setF({ ...f, section: e.target.value })} data-testid="epl-read-section" /></label>
          <label className="block"><span className="nb-label">Subject</span><SubjectSelect className="nb-select mt-1" value={f.subject_name} onChange={(v) => setF({ ...f, subject_name: v })} data-testid="epl-read-subject" /></label>
          <label className="block"><span className="nb-label">Language</span>
            <select className="nb-select mt-1" value={f.language_code} onChange={(e) => setF({ ...f, language_code: e.target.value })} data-testid="epl-read-lang">
              {langs.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
            </select></label>
        </div>
        <div className="flex gap-2">
          <button type="button" onClick={() => setF({ ...f, passage_type: "PASSAGE" })} className={`nb-btn text-xs ${f.passage_type === "PASSAGE" ? "" : "nb-btn-ghost"}`}>📄 Passage</button>
          <button type="button" onClick={() => setF({ ...f, passage_type: "WORD_LIST" })} className={`nb-btn text-xs ${f.passage_type === "WORD_LIST" ? "" : "nb-btn-ghost"}`}>🔤 Word List</button>
        </div>
        <input className="nb-input" placeholder="Title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="epl-read-title" />
        {f.passage_type === "PASSAGE"
          ? <textarea className="nb-textarea" rows={4} placeholder="Passage text" value={f.content_text} onChange={(e) => setF({ ...f, content_text: e.target.value })} data-testid="epl-read-content" />
          : <textarea className="nb-textarea" rows={3} placeholder="cat, dog, sun" value={f.word_items} onChange={(e) => setF({ ...f, word_items: e.target.value })} data-testid="epl-read-words" />}
        <button onClick={createAssignment} className="nb-btn w-full justify-center" data-testid="epl-read-create"><Plus size={16} />Assign to My Section</button>
      </div>
    </div>
  );
}

function AudioPlayer({ url, testid }) {
  const [open, setOpen] = useState(false);
  const [src, setSrc] = useState("");
  const [loading, setLoading] = useState(false);
  useEffect(() => () => { if (src && src.startsWith("blob:")) URL.revokeObjectURL(src); }, [src]);
  if (!url) return <span className="text-xs text-slate-400 italic" data-testid={testid ? `${testid}-none` : undefined}>Audio not available — transcript only</span>;
  const load = async () => {
    setOpen(true);
    if (/^https?:\/\//.test(url)) { setSrc(url); return; }
    setLoading(true);
    try {
      const r = await api.get(`/ponderr-learn/files/serve/${url}`, { responseType: "blob" });
      setSrc(URL.createObjectURL(r.data));
    } catch { toast.error("Could not load recording"); }
    setLoading(false);
  };
  if (!open) return <button onClick={load} className="nb-btn nb-btn-secondary text-xs" data-testid={testid}><Volume2 size={12} />Listen to Recording</button>;
  return <audio src={src} controls preload="auto" className="w-56 max-w-full h-9" data-testid={testid ? `${testid}-player` : undefined}>{loading ? "Loading…" : ""}</audio>;
}

function ReadingRecordModal({ student, passage, settings, onClose, onDone }) {
  const support = speechSupport();
  const words = tokenize(passage.content_text || (passage.word_items || []).join(" "));
  const [statuses, setStatuses] = useState([]);
  const [listening, setListening] = useState(false);
  const [result, setResult] = useState(null);
  const recRef = useRef(null); const startT = useRef(0); const heard = useRef("");
  const recorderRef = useRef(null); const audioBlobRef = useRef(null);
  useEffect(() => { loadVoices(); }, []);
  const gender = settings.default_voice_gender || "female";

  const listen = () => speak(passage.content_text || (passage.word_items || []).join(", "), { lang: passage.language_code, gender, rate: 0.85 });
  const start = async () => {
    if (!support.stt) { toast.error("Speech recognition unavailable here — use ‘Mark as read’."); return; }
    const session = createReadingSession(passage.language_code, {
      onTranscript: (full) => setStatuses(matchWords(words, full).statuses),
    });
    if (!session) { toast.error("Speech recognition unavailable"); return; }
    heard.current = ""; setStatuses([]); setResult(null); audioBlobRef.current = null;
    try { const rec = await createAudioRecorder(); if (rec) { recorderRef.current = rec; rec.start(); } } catch { recorderRef.current = null; }
    recRef.current = session; startT.current = Date.now();
    session.start(); setListening(true);
    toast.message("Listening… have the student read the whole passage, then tap Stop");
  };
  const stop = async () => {
    const session = recRef.current;
    const full = session ? session.stop() : heard.current;
    recRef.current = null; heard.current = full; setListening(false);
    if (recorderRef.current) { try { audioBlobRef.current = await recorderRef.current.stop(); } catch { /* */ } recorderRef.current = null; }
    finish(full);
  };
  const finish = (full) => {
    const elapsed = Math.max(1, (Date.now() - startT.current) / 1000);
    const m = matchWords(words, full);
    const spoken = tokenize(full).length;
    setStatuses(m.statuses);
    setResult({ wpm: Math.round((spoken / elapsed) * 60), acc: m.total ? Math.round((m.correct / m.total) * 100) : 0, correct: m.correct, total: m.total, duration: Math.round(elapsed) });
  };
  const uploadAudio = async () => {
    if (!audioBlobRef.current) return null;
    try {
      const fd = new FormData();
      fd.append("file", new File([audioBlobRef.current], "recording.webm", { type: audioBlobRef.current.type || "audio/webm" }));
      const up = await api.post("/ponderr-learn/files/upload", fd);
      return up.data.storage_path;
    } catch { return null; }
  };
  const submit = async (manual) => {
    const audio_blob_url = manual ? null : await uploadAudio();
    const payload = manual
      ? { recorded_wpm: 0, accuracy_percent: 100, duration_sec: 0, words_total: words.length, words_correct: words.length }
      : { recorded_wpm: result.wpm, accuracy_percent: result.acc, duration_sec: result.duration, words_total: result.total, words_correct: result.correct };
    try {
      const r = await api.post("/ponderr-learn/educator/reading/submit-for-student", { passage_id: passage.passage_id, student_id: student.student_id, audio_blob_url, ...payload });
      toast.success(`Recorded for ${student.name} · AI mark ${r.data.ai_suggested_mark}/${passage.max_marks}`); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={onClose}>
      <div className="nb-card bg-white p-5 max-w-2xl w-full max-h-[90vh] overflow-y-auto space-y-3" onClick={(e) => e.stopPropagation()} data-testid="epl-read-record-modal">
        <div className="flex items-center justify-between">
          <div><div className="nb-label">Record for {student.name}</div><div className="display text-lg font-bold">{passage.title}</div></div>
          <button onClick={onClose} className="nb-btn nb-btn-ghost"><X size={16} /></button>
        </div>
        <div className="flex gap-2">
          <button onClick={listen} className="nb-btn nb-btn-secondary text-xs" data-testid="epl-read-modal-listen"><Volume2 size={14} />Listen</button>
          {!listening ? <button onClick={start} className="nb-btn text-xs" data-testid="epl-read-modal-start"><Mic size={14} />Start Recording</button>
            : <button onClick={stop} className="nb-btn nb-btn-danger text-xs" data-testid="epl-read-modal-stop"><Square size={14} />Stop</button>}
        </div>
        <div className="nb-card p-4 leading-relaxed text-base bg-[#FBF9F6]" data-testid="epl-read-modal-text">
          {words.map((w, i) => <span key={i} className={statuses[i] === "correct" ? "text-emerald-700 font-semibold" : statuses[i] === "wrong" && (result || listening) ? "text-amber-600" : ""}>{w} </span>)}
        </div>
        {result && (
          <div className="nb-card p-3 bg-[#86EFAC] flex flex-wrap gap-4 items-center" data-testid="epl-read-modal-result">
            <div><div className="nb-label">WPM</div><div className="display text-2xl font-black">{result.wpm}</div></div>
            <div><div className="nb-label">Accuracy</div><div className="display text-2xl font-black">{result.acc}%</div></div>
            <button onClick={() => submit(false)} className="nb-btn ml-auto" data-testid="epl-read-modal-submit"><Check size={16} />Submit for Student</button>
          </div>
        )}
        {!support.stt && <div className="text-xs text-amber-700">Microphone reading capture isn’t available in this browser — use “Mark as read”.</div>}
        <button onClick={() => submit(true)} className="nb-btn nb-btn-tertiary w-full justify-center" data-testid="epl-read-modal-selfmark"><Check size={16} />Mark as read (manual)</button>
      </div>
    </div>
  );
}

export function ArtThumb({ path, className }) {
  const [url, setUrl] = useState("");
  useEffect(() => {
    let revoke;
    if (path) {
      api.get(`/ponderr-learn/files/serve/${path}`, { responseType: "blob" })
        .then((r) => { const u = URL.createObjectURL(r.data); setUrl(u); revoke = u; })
        .catch(() => {});
    }
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [path]);
  if (!url) return <div className={`bg-slate-100 grid place-items-center ${className}`}><ImageIcon size={20} className="text-slate-400" /></div>;
  return <img src={url} alt="submission" className={className} />;
}

function EduArt({ profile, testTypes }) {
  const [prompts, setPrompts] = useState([]);
  const [selPid, setSelPid] = useState("");
  const [roster, setRoster] = useState([]);
  const [subs, setSubs] = useState([]);
  const [scores, setScores] = useState({});
  const [sync, setSync] = useState({});
  const [uploading, setUploading] = useState(null); // student being uploaded for

  const loadAssignments = () => api.get("/ponderr-learn/educator/assignments").then((r) => {
    setPrompts(r.data.art_prompts || []);
    if (!selPid && (r.data.art_prompts || []).length) setSelPid(r.data.art_prompts[0].prompt_id);
  }).catch(() => {});
  const loadSubs = () => api.get("/ponderr-learn/educator/art-submissions").then((r) => setSubs(r.data)).catch(() => {});
  useEffect(() => { loadAssignments(); loadSubs(); }, []);
  useEffect(() => {
    if (profile?.class_name && profile?.section) {
      api.get("/students", { params: { class_name: profile.class_name, section: profile.section } })
        .then((r) => setRoster(r.data)).catch(() => {});
    }
  }, [profile]);

  const selPrompt = prompts.find((p) => p.prompt_id === selPid);
  const subFor = (sid) => subs.filter((s) => s.prompt_id === selPid && s.student_id === sid)
    .sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""))[0];

  const approve = async (s) => {
    try {
      await api.post(`/ponderr-learn/educator/art-submissions/${s.submission_id}/approve`, {
        teacher_score: scores[s.submission_id] != null ? Number(scores[s.submission_id]) : null,
        test_type: sync[s.submission_id] || null, subject: "Art & Craft",
      });
      toast.success("Artwork approved"); loadSubs();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const uploadForStudent = async (student, file) => {
    if (!file) return;
    try {
      const fd = new FormData(); fd.append("file", file);
      const up = await api.post("/ponderr-learn/files/upload", fd);
      await api.post("/ponderr-learn/educator/art/submit-for-student", { prompt_id: selPid, student_id: student.student_id, storage_path: up.data.storage_path, mode: "PHOTO" });
      toast.success(`Uploaded for ${student.name}`); setUploading(null); loadSubs();
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); setUploading(null); }
  };

  const [f, setF] = useState({ grade: profile?.class_name || "G01", section: profile?.section || "A", subject_name: "Art", title: "", description: "", submission_type: "BOTH", max_marks: 10 });
  useEffect(() => { setF((s) => ({ ...s, grade: profile?.class_name || s.grade, section: profile?.section || s.section })); }, [profile]);
  const create = async () => {
    if (!f.title.trim()) return toast.error("Title required");
    try { await api.post("/ponderr-learn/art-prompts", { ...f, max_marks: Number(f.max_marks) }); toast.success("Prompt created"); setF({ ...f, title: "", description: "" }); loadAssignments(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="space-y-5">
      <div className="nb-card p-5 space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="display text-lg font-bold">Art & Craft Grading Log</div>
          <label className="flex items-center gap-2 text-xs">Prompt:
            <select className="nb-select text-xs" value={selPid} onChange={(e) => setSelPid(e.target.value)} data-testid="epl-art-assignment">
              {prompts.map((p) => <option key={p.prompt_id} value={p.prompt_id}>{p.title} ({p.grade})</option>)}
            </select>
          </label>
        </div>
        {!selPrompt ? <div className="text-sm text-slate-500">No art prompts for your class yet — create one below.</div> : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {roster.map((stu) => {
              const s = subFor(stu.student_id);
              const status = s?.status || "NOT_SUBMITTED";
              return (
                <div key={stu.student_id} className="nb-card p-3 space-y-2" data-testid={`epl-art-roster-${stu.student_id}`}>
                  {(s?.canvas_image_url || s?.uploaded_image_url || s?.storage_path)
                    ? <ArtThumb path={s.canvas_image_url || s.uploaded_image_url || s.storage_path} className="w-full h-40 object-contain border-2 border-black rounded bg-white" />
                    : <div className="w-full h-40 grid place-items-center border-2 border-dashed border-black/30 rounded bg-slate-50 text-slate-400"><ImageIcon size={22} /></div>}
                  <div className="font-bold text-sm">{stu.name}</div>
                  <span className={`nb-badge ${status === "APPROVED" ? "bg-[#86EFAC]" : status === "PENDING" ? "bg-[#FDE047]" : "bg-slate-200"}`}>{status.replace("_", " ")}</span>
                  {status === "APPROVED" ? null : (
                    <div className="space-y-2">
                      {s && (
                        <div className="flex gap-2 items-center">
                          <input type="number" className="nb-input w-16 text-sm" placeholder="Score" defaultValue={s.teacher_score ?? ""} onChange={(e) => setScores({ ...scores, [s.submission_id]: e.target.value })} data-testid={`epl-art-score-${stu.student_id}`} />
                          <span className="text-xs">/ {s.max_marks}</span>
                          <select className="nb-select text-xs flex-1" onChange={(e) => setSync({ ...sync, [s.submission_id]: e.target.value })} data-testid={`epl-art-tt-${stu.student_id}`}>
                            <option value="">test type</option>
                            {testTypes.map((t) => <option key={t} value={t}>{t}</option>)}
                          </select>
                        </div>
                      )}
                      <div className="flex gap-2">
                        <label className="nb-btn nb-btn-secondary text-xs cursor-pointer flex-1 justify-center">
                          <Camera size={12} />Upload for Student
                          <input type="file" accept="image/*" capture="environment" className="hidden"
                            onChange={(e) => uploadForStudent(stu, e.target.files[0])} data-testid={`epl-art-upload-${stu.student_id}`} />
                        </label>
                        {s && <button onClick={() => approve(s)} className="nb-btn text-xs" data-testid={`epl-art-approve-${stu.student_id}`}><Check size={12} /></button>}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
            {roster.length === 0 && <div className="nb-card p-6 text-slate-500 col-span-full text-sm">No students in your class roster.</div>}
          </div>
        )}
      </div>
      <div className="nb-card p-5 space-y-3">
        <div className="display text-lg font-bold">➕ Create Class Art Prompt</div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <label className="block"><span className="nb-label">Grade</span><GradeSelect className="nb-select mt-1" value={f.grade} onChange={(v) => setF({ ...f, grade: v })} data-testid="epl-art-grade" /></label>
          <label className="block"><span className="nb-label">Section</span><input className="nb-input mt-1" value={f.section} onChange={(e) => setF({ ...f, section: e.target.value })} data-testid="epl-art-section" /></label>
          <label className="block"><span className="nb-label">Subject</span><SubjectSelect className="nb-select mt-1" value={f.subject_name} onChange={(v) => setF({ ...f, subject_name: v })} data-testid="epl-art-subject" /></label>
          <label className="block"><span className="nb-label">Type</span>
            <select className="nb-select mt-1" value={f.submission_type} onChange={(e) => setF({ ...f, submission_type: e.target.value })} data-testid="epl-art-type">
              <option value="CANVAS">Canvas</option><option value="PHOTO">Photo</option><option value="BOTH">Both</option>
            </select></label>
        </div>
        <input className="nb-input" placeholder="Title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="epl-art-title" />
        <textarea className="nb-textarea" rows={3} placeholder="Instructions / brief" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="epl-art-desc" />
        <button onClick={create} className="nb-btn w-full justify-center" data-testid="epl-art-create"><Plus size={16} />Assign to My Section</button>
      </div>
    </div>
  );
}

function EduNotes({ profile, settings }) {
  const [globalNotes, setGlobalNotes] = useState([]);
  const [f, setF] = useState({ grade: profile?.class_name || "G01", section: profile?.section || "A", subject_name: profile?.subject || "English", title: "", body_html: "" });
  useEffect(() => {
    api.get("/ponderr-learn/notes").then((r) => setGlobalNotes(r.data)).catch(() => {});
  }, []);
  useEffect(() => { setF((s) => ({ ...s, grade: profile?.class_name || s.grade, section: profile?.section || s.section, subject_name: profile?.subject || s.subject_name })); }, [profile]);
  const create = async () => {
    if (!f.title.trim()) return toast.error("Title required");
    try { await api.post("/ponderr-learn/notes", { ...f, publish: true, translations: [] }); toast.success("Supplementary note published to your class"); setF({ ...f, title: "", body_html: "" }); api.get("/ponderr-learn/notes").then((r) => setGlobalNotes(r.data)); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="grid lg:grid-cols-2 gap-5">
      <div className="nb-card p-5 space-y-2">
        <div className="display text-lg font-bold">Master & Class Notes</div>
        {globalNotes.length === 0 && <div className="text-sm text-slate-500">No notes yet.</div>}
        {globalNotes.map((n) => (
          <div key={n.note_id} className="nb-card-sm p-3 bg-[#C4B5FD]" data-testid={`epl-note-${n.note_id}`}>
            <div className="flex items-center gap-2"><span className="nb-badge bg-white text-xs">{n.is_global ? "GLOBAL" : "CLASS"}</span><b>{n.title}</b></div>
            <div className="text-xs mono">{n.grade} · {n.subject_name}</div>
          </div>
        ))}
      </div>
      <div className="nb-card p-5 space-y-3">
        <div className="display text-lg font-bold">➕ Create Supplementary Note</div>
        <div className="grid grid-cols-3 gap-3">
          <label className="block"><span className="nb-label">Grade</span><GradeSelect className="nb-select mt-1" value={f.grade} onChange={(v) => setF({ ...f, grade: v })} data-testid="epl-note-grade" /></label>
          <label className="block"><span className="nb-label">Section</span><input className="nb-input mt-1" value={f.section} onChange={(e) => setF({ ...f, section: e.target.value })} data-testid="epl-note-section" /></label>
          <label className="block"><span className="nb-label">Subject</span><SubjectSelect className="nb-select mt-1" value={f.subject_name} onChange={(v) => setF({ ...f, subject_name: v })} data-testid="epl-note-subject" /></label>
        </div>
        <input className="nb-input" placeholder="Title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="epl-note-title" />
        <textarea className="nb-textarea" rows={6} placeholder="Note body (HTML allowed)" value={f.body_html} onChange={(e) => setF({ ...f, body_html: e.target.value })} data-testid="epl-note-body" />
        <button onClick={create} className="nb-btn w-full justify-center" data-testid="epl-note-create"><Plus size={16} />Publish to My Class</button>
      </div>
    </div>
  );
}
